import { Slider, SliderProps } from "@mui/material";
import {
  CoverageNeed,
  ProductCoverageName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import { Field, FieldProps, useFormikContext } from "formik";
import { SliderWrapper, sliderStyle } from "./styled";

import { Mark } from "@mui/base";
import { VALUE_TYPE } from "Components/NumberValue";
import { formatToLocaleString } from "Formatting/formatNumber";
import { getPaymentRestriction } from "Store/akaAdvice/interfaces/getters";
import { getCorrectAkaAdviceValues } from "Store/akaAdvice/mappers/mapToFormValues/utils";
import { isDefined } from "Utils/functions";
import React from "react";

interface Props {
  name: string;
  isAdvisor: boolean;
  need: CoverageNeed;
  productCoverageName: ProductCoverageName;
  step: number | null;
  adviceValueMarkLabel: string | undefined;
  valueType: VALUE_TYPE;
  disabled?: boolean;
  label: string;
  onChange?: (e: Event, value: number) => void;
  onChangeCommitted?: (e: React.SyntheticEvent | Event, value: number) => void;
  withLeeway?: boolean;
}

/**
 * Renders a discrete slider based on @mui/material Slider.
 */
export const AkaDiscreteSlider = ({
  name,
  isAdvisor,
  need,
  productCoverageName,
  step,
  adviceValueMarkLabel,
  valueType,
  disabled,
  label,
  onChange,
  onChangeCommitted,
  withLeeway = false,
}: Props): React.ReactElement => {
  const formikContext = useFormikContext<Record<string, unknown>>();

  const { adviceValue, minAdviceValue, maxAdviceValue } =
    getCorrectAkaAdviceValues(need, productCoverageName, isAdvisor);
  const restrictions = getPaymentRestriction(need, productCoverageName);
  const min = isAdvisor
    ? restrictions.minPayouts.PRODUCT_MANAGER
    : restrictions.minPayouts.CUSTOMER;
  const max = isAdvisor
    ? restrictions.maxPayouts.PRODUCT_MANAGER
    : restrictions.maxPayouts.CUSTOMER;

  const trackLeft = (): number => {
    const value = withLeeway
      ? ((minAdviceValue - min) * 100) / (max - min)
      : ((adviceValue - min) * 100) / (max - min);
    return Math.round(Math.max(value, 0));
  };

  const trackWidth = (): number => {
    const value = withLeeway
      ? ((maxAdviceValue - min) * 100) / (max - min)
      : ((adviceValue - min) * 100) / (max - min);
    return Math.round(Math.max(value, 0)) - trackLeft();
  };

  const valuetext = (value: number): string => {
    return `${formatToLocaleString(value)} ${valueType}`;
  };

  const sx: Required<SliderProps["sx"]> = {
    ...sliderStyle,
    "& .MuiSlider-track": {
      height: 4,
      left: `${trackLeft()}% !important`,
      width: `${trackWidth()}% !important`,
    },
  };

  const marks: Mark[] = [
    {
      value: min,
      label: `${formatToLocaleString(min)} ${valueType}`,
    },
  ];
  if (minAdviceValue !== null && maxAdviceValue !== null) {
    marks.push(
      { value: minAdviceValue },
      { value: adviceValue, label: adviceValueMarkLabel },
      { value: maxAdviceValue },
    );
  }
  marks.push({
    value: max,
    label: `${formatToLocaleString(max)} ${valueType}`,
  });

  return (
    <Field name={name} type="range">
      {({ field }: FieldProps) => (
        <SliderWrapper
          className="AdviceSlider"
          extended={isDefined(adviceValueMarkLabel)}
        >
          <Slider
            {...field}
            sx={sx}
            getAriaValueText={valuetext}
            onChange={(e, value) => {
              formikContext.setFieldValue(name, value);
              onChange && onChange(e, Number(value));
            }}
            onChangeCommitted={(e, value) => {
              formikContext.setFieldValue(name, value);
              formikContext.validateForm({
                ...formikContext.values,
                [name]: value,
              });
              onChangeCommitted && onChangeCommitted(e, Number(value));
            }}
            disabled={disabled}
            valueLabelFormat={valuetext}
            aria-label={label}
            valueLabelDisplay="on"
            step={step}
            marks={marks}
            min={min}
            max={max}
          />
        </SliderWrapper>
      )}
    </Field>
  );
};
